import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SlideOver, SlideOverFooter } from './SlideOver';
import { Input } from './Input';
import { FormField } from './FormField';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './Tabs';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from './Table';
import { Badge } from './Badge';
import { formatDate, isoDate } from '../../utils/dateUtils';
import { parseAxiosError, translateApiError } from '../../errors';
import type {
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelDto,
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelVersionDto,
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkDayDto,
  SystemDayOfWeek,
} from '../../api/generated/models';

interface WorkScheduleModelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Resolves false when the save was cancelled (e.g. in the change preview); the dialog then stays open. */
  onSave: (data: WorkScheduleModelFormData) => Promise<void | boolean>;
  model?: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelDto;
  mode: 'create' | 'edit';
}

export interface WorkScheduleModelFormData {
  name: string;
  weeklyHours: number;
  isActive: boolean;
  workDays: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkDayDto[];
  /** ISO date (YYYY-MM-DD) from which the week plan applies; edit mode only. */
  validFrom: string;
}

function generateEmptyWeek(): FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkDayDto[] {
  return [1, 2, 3, 4, 5, 6, 0].map((dayOfWeek) => ({
    dayOfWeek: dayOfWeek as SystemDayOfWeek,
    isWorkingDay: false,
    startTime: null,
    endTime: null,
    breakMinutes: 0,
    isBreakPaid: false,
  }));
}

type WorkDay = FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkDayDto;

// Paid break (e.g. 40h) counts as working time; unpaid (e.g. 38.5h) is deducted.
function calculateDailyHours(day: WorkDay, isBreakPaid: boolean): number {
  if (!day.isWorkingDay || !day.startTime || !day.endTime) {
    return 0;
  }

  const [startHour, startMinute] = day.startTime.split(':').map(Number);
  const [endHour, endMinute] = day.endTime.split(':').map(Number);
  const workMinutes = endHour * 60 + endMinute - (startHour * 60 + startMinute);
  const netMinutes = isBreakPaid ? workMinutes : workMinutes - (day.breakMinutes || 0);

  return Math.max(0, netMinutes / 60);
}

function calculateWeeklyHours(workDays: WorkDay[], isBreakPaid: boolean): number {
  const total = workDays.reduce((sum, day) => sum + calculateDailyHours(day, isBreakPaid), 0);
  return Math.round(total * 100) / 100;
}

// The model sets the paid break per version for the whole week.
function isVersionBreakPaid(workDays: WorkDay[] | null | undefined): boolean {
  return workDays?.some((d) => d.isWorkingDay && d.isBreakPaid) ?? false;
}

// A version may omit days off — fill the week so every weekday has a row.
function completeWeek(
  workDays: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkDayDto[] | null | undefined
): FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkDayDto[] {
  return generateEmptyWeek().map(
    (empty) => workDays?.find((d) => d.dayOfWeek === empty.dayOfWeek) ?? empty
  );
}

// The newest version when it lies in the future — editing then continues that planned change.
function plannedVersion(
  model: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelDto | undefined,
  today: string
): FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelVersionDto | undefined {
  const latest = model?.versions?.[model.versions.length - 1];
  return latest?.validFrom && latest.validFrom > today ? latest : undefined;
}

export function WorkScheduleModelDialog({
  isOpen,
  onClose,
  onSave,
  model,
  mode,
}: WorkScheduleModelDialogProps) {
  const { t } = useTranslation(['workScheduleModels', 'common']);
  
  // Use getter for reactive translation
  const DAY_NAMES: Record<number, string> = {
    get 1() { return t('workScheduleModels:weekdays.monday'); },
    get 2() { return t('workScheduleModels:weekdays.tuesday'); },
    get 3() { return t('workScheduleModels:weekdays.wednesday'); },
    get 4() { return t('workScheduleModels:weekdays.thursday'); },
    get 5() { return t('workScheduleModels:weekdays.friday'); },
    get 6() { return t('workScheduleModels:weekdays.saturday'); },
    get 0() { return t('workScheduleModels:weekdays.sunday'); },
  };
  
  const today = isoDate(new Date());
  // A past valid-from is allowed at most a year back (FAFD-67, planning.workScheduleModels.validFromTooFarBack).
  const oneYearBack = (() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return isoDate(date);
  })();
  const planned = mode === 'edit' ? plannedVersion(model, today) : undefined;

  const [formData, setFormData] = useState<WorkScheduleModelFormData>({
    name: '',
    weeklyHours: 40,
    isActive: true,
    workDays: generateEmptyWeek(),
    validFrom: today,
  });
  const [isBreakPaid, setIsBreakPaid] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const now = isoDate(new Date());
      if (mode === 'edit' && model) {
        const upcoming = plannedVersion(model, now);
        const workDays = upcoming ? upcoming.workDays : model.workDays;
        setFormData({
          name: model.name || '',
          weeklyHours: model.weeklyHours || 40,
          isActive: model.isActive ?? true,
          workDays: completeWeek(workDays),
          // why: today, not the current version's date — prefilling a past date would turn every
          // ordinary edit into a retroactive one. "gilt seit …" below shows the real date (FAFD-67).
          validFrom: upcoming?.validFrom ?? now,
        });
        setIsBreakPaid(isVersionBreakPaid(workDays));
      } else {
        setFormData({
          name: '',
          weeklyHours: 40,
          isActive: true,
          workDays: generateEmptyWeek(),
          validFrom: now,
        });
        setIsBreakPaid(false);
      }
      setErrors([]);
      setActiveTab('edit');
    }
  }, [isOpen, mode, model]);

  const validateForm = (): string[] => {
    const validationErrors: string[] = [];

    if (!formData.name || formData.name.trim().length === 0) {
      validationErrors.push(t('workScheduleModels:form.nameRequired'));
    }
    if (formData.name.length > 100) {
      validationErrors.push(t('workScheduleModels:form.nameTooLong'));
    }

    if (formData.weeklyHours < 0.1 || formData.weeklyHours > 168) {
      validationErrors.push(t('workScheduleModels:form.weeklyHoursInvalid'));
    }

    if (mode === 'edit') {
      if (!formData.validFrom) {
        validationErrors.push(t('workScheduleModels:form.validFromRequired'));
      }
    }

    const workingDays = formData.workDays.filter((d) => d.isWorkingDay);
    if (workingDays.length === 0) {
      validationErrors.push(t('workScheduleModels:form.atLeastOneWorkDay'));
    }

    formData.workDays.forEach((day) => {
      if (day.isWorkingDay) {
        if (!day.startTime || !day.endTime) {
          validationErrors.push(
            `${DAY_NAMES[day.dayOfWeek || 0]}: ${t('workScheduleModels:form.startEndRequired')}`
          );
        } else if (day.startTime >= day.endTime) {
          validationErrors.push(
            `${DAY_NAMES[day.dayOfWeek || 0]}: ${t('workScheduleModels:form.startBeforeEnd')}`
          );
        }
      }
    });

    return validationErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const saved = await onSave({
        ...formData,
        workDays: formData.workDays.map((day) => ({ ...day, isBreakPaid })),
      });
      if (saved !== false) {
        onClose();
      }
    } catch (error) {
      setErrors([translateApiError(parseAxiosError(error)) || t('workScheduleModels:errors.saveError')]);
    } finally {
      setSaving(false);
    }
  };

  const updateWorkDay = (
    dayOfWeek: number,
    updates: Partial<FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkDayDto>
  ) => {
    setFormData((prev) => {
      const updatedWorkDays = prev.workDays.map((day) =>
        day.dayOfWeek === dayOfWeek ? { ...day, ...updates } : day
      );

      return {
        ...prev,
        workDays: updatedWorkDays,
        weeklyHours: calculateWeeklyHours(updatedWorkDays, isBreakPaid),
      };
    });
  };

  const updateBreakPaid = (paid: boolean) => {
    setIsBreakPaid(paid);
    setFormData((prev) => ({ ...prev, weeklyHours: calculateWeeklyHours(prev.workDays, paid) }));
  };

  const editFields = (
    <>
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label={t('workScheduleModels:form.nameLabel')} required>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('workScheduleModels:form.namePlaceholder')}
            maxLength={100}
          />
        </FormField>
        <FormField label={t('workScheduleModels:form.weeklyHoursLabel')}>
          <div className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md text-[13px] text-slate-700 tabular-nums">
            {formData.weeklyHours.toFixed(2)} h
          </div>
        </FormField>
      </div>

      {/* Paid break — decides whether the break counts as working time (§ 11 AZG) */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isBreakPaid"
            checked={isBreakPaid}
            onChange={(e) => updateBreakPaid(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
          />
          <label htmlFor="isBreakPaid" className="text-[13px] text-slate-700">
            {t('workScheduleModels:form.breakPaidLabel')}
          </label>
        </div>
        <p className="text-[12px] text-slate-500 pl-6">
          {t(isBreakPaid ? 'workScheduleModels:form.breakPaidHint' : 'workScheduleModels:form.breakUnpaidHint')}
        </p>
      </div>

      {/* Active Toggle */}
      {mode === 'edit' && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            className="w-4 h-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
          />
          <label htmlFor="isActive" className="text-[13px] text-slate-700">
            {t('workScheduleModels:dialog.active')}
          </label>
        </div>
      )}

      {/* Valid from — the week plan applies from this date (FAFD-25) */}
      {mode === 'edit' && (
        <div className="space-y-2">
          <FormField
            label={t('workScheduleModels:form.validFromLabel')}
            required
            helperText={t('workScheduleModels:form.validFromHint')}
          >
            <Input
              type="date"
              value={formData.validFrom}
              min={oneYearBack}
              onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
            />
          </FormField>
          {model && (
            <p className="text-[12px] text-slate-500">
              {model.validFrom
                ? t('workScheduleModels:form.currentSince', { date: formatDate(model.validFrom) })
                : t('workScheduleModels:form.currentSinceBeginning')}
            </p>
          )}
          {/* A past date corrects worked days too (FAFD-67); the page shows a preview before saving. */}
          {formData.validFrom && formData.validFrom < today && (
            <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              {t('workScheduleModels:form.validFromPastHint')}
            </p>
          )}
          {planned && (
            <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              {t('workScheduleModels:form.plannedVersionHint', { date: formatDate(planned.validFrom) })}
            </p>
          )}
        </div>
      )}

      {/* Week Plan */}
      <div>
        <h3 className="text-[15px] font-semibold text-slate-900 tracking-tight mb-3">
          {t('workScheduleModels:dialog.weekPlan')}
        </h3>
        <div className="space-y-2">
          {formData.workDays.map((day) => (
            <div
              key={day.dayOfWeek}
              className="border border-slate-200 rounded-xl p-3"
            >
              <div className="flex items-start gap-4">
                {/* Checkbox */}
                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    id={`day-${day.dayOfWeek}`}
                    checked={day.isWorkingDay || false}
                    onChange={(e) =>
                      updateWorkDay(day.dayOfWeek || 0, {
                        isWorkingDay: e.target.checked,
                        startTime: e.target.checked ? '08:00' : null,
                        endTime: e.target.checked ? '16:30' : null,
                        breakMinutes: e.target.checked ? 30 : 0,
                      })
                    }
                    className="w-4 h-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
                  />
                </div>

                {/* Day Name */}
                <label
                  htmlFor={`day-${day.dayOfWeek}`}
                  className="min-w-[100px] pt-2 text-[13px] font-medium text-slate-900"
                >
                  {DAY_NAMES[day.dayOfWeek || 0]}
                </label>

                {/* Times */}
                {day.isWorkingDay ? (
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1">
                        {t('workScheduleModels:form.fromLabel')}
                      </label>
                      <Input
                        type="time"
                        value={day.startTime?.substring(0, 5) || ''}
                        onChange={(e) =>
                          updateWorkDay(day.dayOfWeek || 0, {
                            startTime: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1">
                        {t('workScheduleModels:form.toLabel')}
                      </label>
                      <Input
                        type="time"
                        value={day.endTime?.substring(0, 5) || ''}
                        onChange={(e) =>
                          updateWorkDay(day.dayOfWeek || 0, {
                            endTime: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1">
                        {t('workScheduleModels:form.breakLabel')}
                      </label>
                      <Input
                        type="number"
                        value={day.breakMinutes || 0}
                        onChange={(e) =>
                          updateWorkDay(day.dayOfWeek || 0, {
                            breakMinutes: parseInt(e.target.value) || 0,
                          })
                        }
                        min="0"
                        max="480"
                      />
                    </div>
                  </div>
                ) : (
                  <span className="text-[13px] text-slate-500 pt-2">
                    {t('workScheduleModels:dialog.free')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  // Newest first; the version in effect today is the newest one not after today.
  const versions = [...(model?.versions ?? [])].reverse();
  const currentIndex = versions.findIndex((v) => !v.validFrom || v.validFrom <= today);
  const history = versions.length === 0 ? (
    <p className="text-[13px] text-slate-500">{t('workScheduleModels:history.empty')}</p>
  ) : (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>{t('workScheduleModels:history.validFrom')}</TableHeader>
          <TableHeader>{t('workScheduleModels:history.weeklyHours')}</TableHeader>
          <TableHeader>{t('workScheduleModels:history.workDays')}</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {versions.map((version, index) => (
          <TableRow key={version.validFrom ?? 'start'}>
            <TableCell>
              <div className="text-[13px] text-slate-900 tabular-nums">
                {version.validFrom
                  ? formatDate(version.validFrom)
                  : t('workScheduleModels:history.sinceBeginning')}
              </div>
              <Badge
                variant={index < currentIndex ? 'warning' : index === currentIndex ? 'success' : 'default'}
                className="mt-1"
              >
                {index < currentIndex
                  ? t('workScheduleModels:history.planned')
                  : index === currentIndex
                    ? t('workScheduleModels:history.current')
                    : t('workScheduleModels:history.past')}
              </Badge>
            </TableCell>
            <TableCell>
              <span className="text-[13px] text-slate-900 tabular-nums">
                {(version.weeklyHours ?? 0).toFixed(2)} {t('workScheduleModels:table.hours')}
              </span>
              {isVersionBreakPaid(version.workDays) && (
                <Badge variant="default" className="mt-1 block w-fit">
                  {t('workScheduleModels:breakPaidBadge')}
                </Badge>
              )}
            </TableCell>
            <TableCell>
              <ul className="text-[12px] text-slate-600 space-y-0.5">
                {completeWeek(version.workDays)
                  .filter((d) => d.isWorkingDay)
                  .map((d) => (
                    <li key={d.dayOfWeek} className="tabular-nums">
                      {DAY_NAMES[d.dayOfWeek || 0]} {d.startTime?.substring(0, 5)}–{d.endTime?.substring(0, 5)}
                    </li>
                  ))}
              </ul>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? t('workScheduleModels:dialog.createTitle') : t('workScheduleModels:dialog.editTitle')}
      size="xl"
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
          confirmText={mode === 'create' ? t('workScheduleModels:buttons.create') : t('workScheduleModels:buttons.save')}
          loading={saving}
        />
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <h3 className="text-[12px] font-semibold text-red-800 mb-1">
              {t('workScheduleModels:dialog.errorTitle')}
            </h3>
            <ul className="list-disc list-inside text-[12px] text-red-700 space-y-0.5">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {mode === 'edit' ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="edit">{t('workScheduleModels:dialog.tabs.edit')}</TabsTrigger>
              <TabsTrigger value="history">{t('workScheduleModels:dialog.tabs.history')}</TabsTrigger>
            </TabsList>
            <TabsContent value="edit" className="space-y-6">
              {editFields}
            </TabsContent>
            <TabsContent value="history">
              {history}
            </TabsContent>
          </Tabs>
        ) : (
          editFields
        )}
      </form>
    </SlideOver>
  );
}
